<script setup lang="ts">
/**
 * A chat member's own step list (os.set_steps): the steps it has lined up for the current request, in order, each
 * with its title and the word for its state, drawn through ZhStatusPill so the state never rests on colour alone.
 *
 * - To do and Skipped are neutral, Under way is running, Done is completed (STATUS_TONES).
 * - A skipped step's title is muted and struck through (`data-skipped`), so it stays in the list as the step the
 *   person dropped rather than vanishing.
 * - An empty list draws nothing at all: no panel, no empty state.
 * - `collapsed` starts it as a one-line summary ("2 of 5 done · Under way: …") with a toggle that opens the list,
 *   for a small screen. The summary names the step under way, or else the next one to do.
 * - `label` is also its visible heading, so a sighted person sees whose list it is: above the list, or at the start
 *   of the collapsed summary. Above the list it is hidden from a screen reader, which hears the same words as the
 *   list's own name. `hint` is an optional line under the open list saying how to change it.
 * - The open list is capped in height and scrolls inside itself, so a long list never pushes the conversation off
 *   the screen.
 * - The summary is also kept in a polite live region, so a screen reader hears the list move as the member works.
 */
import { computed, ref, useId, watch } from 'vue'
import type { StatusTone } from '../tokens'
import ZhStatusPill from './ZhStatusPill.vue'

export type StepListItem = { title: string; state: 'todo' | 'in_progress' | 'done' | 'skipped' }

const props = withDefaults(
    defineProps<{
        steps: readonly StepListItem[]
        /** One-line summary with a toggle, for a small screen. */
        collapsed?: boolean
        /** The list's accessible name, and its visible heading. */
        label?: string
        /** A line under the open list saying how the person changes it; none when omitted. */
        hint?: string
    }>(),
    { collapsed: false, label: 'Steps lined up' },
)

/** The word each state reads as: the same four words the chat docs use. */
const STATE_WORD: Readonly<Record<StepListItem['state'], string>> = {
    todo: 'To do',
    in_progress: 'Under way',
    done: 'Done',
    skipped: 'Skipped',
}

const STATE_TONE: Readonly<Record<StepListItem['state'], StatusTone>> = {
    todo: 'neutral',
    in_progress: 'running',
    done: 'completed',
    skipped: 'neutral',
}

const listId = `zh-step-list-${useId()}`

/** Whether the list itself is shown: always when not collapsed, and once the toggle opens it when it is. */
const open = ref(!props.collapsed)
watch(
    () => props.collapsed,
    collapsed => {
        open.value = !collapsed
    },
)

/** What the summary names after the count: the step under way, or else the next one to do, or nothing. */
function summaryTail(steps: readonly StepListItem[]): string | null {
    const current = steps.find(step => step.state === 'in_progress')
    if (current) {
        return `Under way: ${current.title}`
    }
    const next = steps.find(step => step.state === 'todo')
    return next ? `Next: ${next.title}` : null
}

const summary = computed(() => {
    const done = props.steps.filter(step => step.state === 'done').length
    const tail = summaryTail(props.steps)
    return [`${done} of ${props.steps.length} done`, tail].filter(part => part !== null).join(' · ')
})
</script>

<template>
    <div v-if="steps.length > 0" class="zh-step-list" :data-collapsed="collapsed ? 'true' : 'false'">
        <button
            v-if="collapsed"
            type="button"
            class="zh-step-list__toggle"
            :aria-expanded="open ? 'true' : 'false'"
            :aria-controls="listId"
            @click="open = !open"
        >
            <span class="zh-step-list__line">
                <span class="zh-step-list__heading">{{ label }}</span>
                {{ ' ' }}
                <span class="zh-step-list__summary">{{ summary }}</span>
            </span>
            <span class="zh-step-list__chevron" aria-hidden="true">{{ open ? '▴' : '▾' }}</span>
        </button>
        <p v-else class="zh-step-list__heading" aria-hidden="true">{{ label }}</p>
        <ol v-if="open" :id="listId" class="zh-step-list__items" :aria-label="label" tabindex="0">
            <li v-for="(step, index) in steps" :key="index" class="zh-step-list__item" :data-state="step.state">
                <ZhStatusPill :tone="STATE_TONE[step.state]">{{ STATE_WORD[step.state] }}</ZhStatusPill>
                <span class="zh-step-list__title" :data-skipped="step.state === 'skipped' ? 'true' : undefined">{{
                    step.title
                }}</span>
            </li>
        </ol>
        <p v-if="open && hint" class="zh-step-list__hint">{{ hint }}</p>
        <p class="zh-step-list__live" role="status" aria-live="polite">{{ summary }}</p>
    </div>
</template>

<style scoped>
.zh-step-list {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    color: var(--foreground);
    font-size: 0.875rem;
}

/* The one-line summary: the whole row is the toggle, so it is easy to tap on a phone. */
.zh-step-list__toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    width: 100%;
    min-height: 2.25rem;
    border: 1px solid var(--border);
    background: transparent;
    padding: 0.25rem 0.625rem;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
}

.zh-step-list__toggle:focus-visible {
    outline: 2px solid var(--ring);
    outline-offset: 2px;
}

.zh-step-list__line {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Whose list it is, read before anything in it. */
.zh-step-list__heading {
    margin: 0;
    color: var(--muted-foreground);
    font-size: 0.75rem;
    font-weight: 600;
}

.zh-step-list__summary {
    min-width: 0;
}

/* How the person changes the list: they tell the member, since nobody else writes it. */
.zh-step-list__hint {
    margin: 0;
    color: var(--muted-foreground);
    font-size: 0.75rem;
}

/* The summary again, for a screen reader only: it hears the list move as the member works. */
.zh-step-list__live {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
    border: 0;
}

.zh-step-list__chevron {
    flex-shrink: 0;
    color: var(--muted-foreground);
}

/* Capped, and it scrolls inside itself: up to 20 steps never push the conversation off a small screen. */
.zh-step-list__items {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    max-height: min(12rem, 30vh);
    margin: 0;
    padding: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    list-style: none;
}

.zh-step-list__items:focus-visible {
    outline: 2px solid var(--ring);
    outline-offset: 2px;
}

.zh-step-list__item {
    display: flex;
    flex-shrink: 0;
    align-items: baseline;
    gap: 0.625rem;
    min-width: 0;
}

.zh-step-list__item :deep(.zh-status-pill) {
    flex-shrink: 0;
}

.zh-step-list__title {
    min-width: 0;
    overflow-wrap: anywhere;
}

/* A skipped step stays in the list, muted and struck through, so the person sees it was dropped. */
.zh-step-list__title[data-skipped='true'] {
    color: var(--muted-foreground);
    text-decoration: line-through;
}
</style>
